import React, { useRef, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
    FormLabelComposer,
    FormErrorComposer,
    TextInput,
    ToggleSwitch,
} from '@fluid/form-elements';
import {
    getDataAttributes,
    getCSSClasses,
    getLocaleText,
    getInputValue,
    maskText,
} from '../../utils/LoginUtils';
import {
    shouldRememberUserName,
    saveUserName,
    toggleRememberMe,
    getCurrentUsername,
} from '../../utils/RememberMeUtils';

import LoginContext from '../../context/LoginContext';

const TextInputWithLabel = FormLabelComposer(FormErrorComposer(TextInput));
const ToggleSwitchWithLabel = FormLabelComposer(ToggleSwitch);
const BASE_CLS = 'username-field';
const DISPLAY_USER_NAME = 'janedoe';

const UserNameField = (props) => {
    const {
        loginConfig,
        isInteractive,
        locale,
        onUsernameChange,
        username,
        isOAuth2Flow,
    } = props;
    const { userNameGroup, rememberMeGroup } = loginConfig;
    const { userNameLabel, dataAttributes, cssClasses } = userNameGroup ?? {};

    const { maskCharacter, maskCharactersLength, isMaskLeading } =
        userNameGroup ?? {};

    const { isRememberMeEnabled, rememberMeLinkLabel } = rememberMeGroup ?? {};

    const label = getLocaleText(userNameLabel, locale);
    const rememberMeLabel = getLocaleText(rememberMeLinkLabel, locale);

    const [currentRememberMe, setRememberMe] = useState(
        shouldRememberUserName() && !!getCurrentUsername(),
    );
    const [displayUsername, setDisplayUsername] = useState('');
    const [isUserNameMasked, setUserNameMasked] = useState(false);
    const { context, setContext } = useContext(LoginContext);
    const rememberMeRef = useRef();
    const rootRef = useRef();

    const handleRememberMeToggle = () => {
        toggleRememberMe(currentRememberMe);

        if (currentRememberMe && isUserNameMasked) {
            setUserNameMasked(false);
            setDisplayUsername('');
            onUsernameChange('');
        }
        setRememberMe(!currentRememberMe);
    };
    const handleChange = (e) => {
        const value = getInputValue(e);

        if (isUserNameMasked) {
            setUserNameMasked(false);
            setDisplayUsername('');
        }

        if (onUsernameChange) {
            setContext({ ...context, invalidUsername: !value });
            onUsernameChange(value);
        }
    };

    const handleBeforeInput = () => {
        if (isUserNameMasked) {
            saveUserName('', loginConfig);
            setUserNameMasked(false);
            setDisplayUsername('');
            onUsernameChange('');
        }
    };

    const handleKeyDown = (e) => {
        const { keyCode } = e ?? {};

        if (keyCode === 46 || keyCode === 8) {
            handleBeforeInput();
        }
    };

    useEffect(() => {
        if (shouldRememberUserName() && !getCurrentUsername()) {
            // clear toggle switch if the saved username is empty
            toggleRememberMe(true);
        }
    }, []);

    useEffect(() => {
        if (isRememberMeEnabled && !isOAuth2Flow) {
            let currentUsername = '';

            if (isInteractive) {
                if (currentRememberMe) {
                    currentUsername = getCurrentUsername();

                    if (onUsernameChange) {
                        onUsernameChange(currentUsername);
                    }
                }
            } else {
                currentUsername = DISPLAY_USER_NAME;
            }

            if (maskCharactersLength > 0) {
                const displayValue = maskText(
                    currentUsername,
                    maskCharactersLength,
                    maskCharacter,
                    isMaskLeading,
                );
                setDisplayUsername(displayValue);
                setUserNameMasked(true);
            } else {
                setDisplayUsername(currentUsername);
                setUserNameMasked(false);
            }
        } else {
            setDisplayUsername(username);
        }
    }, [
        maskCharactersLength,
        maskCharacter,
        isMaskLeading,
        isRememberMeEnabled,
    ]);

    useEffect(() => {
        if (rememberMeRef.current) {
            rootRef.current.style.setProperty(
                '--username-padding',
                `${rememberMeRef.current.offsetWidth + 15}px`,
            );
        }
    }, [rememberMeLabel]);

    // Defining callsign keystroke field using css class
    // https://docs.callsign.com/docs/web-intel-sdk-registered/287a7228a1bcb-setting-up-the-web-sdk#defining-the-keystroke-fields
    useEffect(() => {
        const usernameInput = rootRef.current.querySelector('#username');

        if (usernameInput) {
            usernameInput.classList.add('cs-username');
        }
    }, []);

    const usernameProps = {
        name: 'userid',
        autoComplete: 'off',
        // id, autoCapitalize, autoCorrect and spellCheck attributes are must for callsign data collection to work
        id: 'username',
        autoCapitalize: 'none',
        autoCorrect: 'off',
        spellcheck: 'false',
        contextCls: getCSSClasses(BASE_CLS, cssClasses),
        labelText: label,
        isStacked: true,
        ...getDataAttributes(dataAttributes),
        value: isUserNameMasked || !isInteractive ? displayUsername : username,
    };
    const rememberMeProps = {
        labelText: rememberMeLabel,
    };

    if (!isInteractive) {
        usernameProps.tabIndex = -1;
        usernameProps.isReadOnly = true;
        rememberMeProps.tabIndex = -1;
        rememberMeProps.isReadOnly = true;
    } else {
        usernameProps.onChange = handleChange;
        usernameProps.onBeforeInput = handleBeforeInput;
        usernameProps.onKeyDown = handleKeyDown;
        rememberMeProps.onChange = handleRememberMeToggle;
        rememberMeProps.isChecked = currentRememberMe;
    }

    return (
        <div ref={rootRef}>
            <TextInputWithLabel {...usernameProps} />
            {isRememberMeEnabled && !isOAuth2Flow && (
                <div ref={rememberMeRef} className={`${BASE_CLS}__remember-me`}>
                    <ToggleSwitchWithLabel {...rememberMeProps} />
                </div>
            )}
        </div>
    );
};

UserNameField.defaultProps = {};

UserNameField.propTypes = {
    locale: PropTypes.string.isRequired,
    loginConfig: PropTypes.object.isRequired,
    isInteractive: PropTypes.bool,
    isOAuth2Flow: PropTypes.bool,
    username: PropTypes.string,
    onUsernameChange: PropTypes.func,
};

export default UserNameField;
